import type { HUD } from '../../timing/HUD';
import type { Minimap } from '../../timing/Minimap';
import type { TitleScreen } from '../../ui/Title';
import type { PauseScreen } from '../../ui/Pause';
import type { CameraToggle } from '../../ui/CameraToggle';
import type { MusicToggle } from '../../ui/MusicToggle';
import type { AudioToggle } from '../../ui/AudioToggle';
import type { SettingsScreen } from '../../ui/Settings';
import type { LeaderboardScreen } from '../../ui/Leaderboard';
import type { ClaimScreen } from '../../ui/Claim';
import type { System } from '../ServiceLocator';

/**
 * Owns the reference graph for all UI screens + the show/hide
 * choreography across mode transitions. Construction stays in
 * bootstrap because each screen is built at a different point in
 * the boot pipeline (TitleScreen early so it can pre-paint while
 * the renderer/track load; HUD after lapTimer; Minimap after
 * checkpoints; etc.). The setters here let bootstrap push each
 * reference in as it's built.
 *
 * Mode-transition methods bundle the UI half of the t.toRacing /
 * t.toTitle / t.toPaused / t.fromPaused state transitions. The
 * audio / DRS / shadow / wake-lock work stays in bootstrap.
 */
export class UISystem implements System {
  readonly name = 'UISystem';

  hud!: HUD;
  minimap!: Minimap;
  title!: TitleScreen;
  pause!: PauseScreen;
  cameraToggle!: CameraToggle;
  musicToggle?: MusicToggle;
  audioToggle?: AudioToggle;
  settings?: SettingsScreen;
  leaderboard?: LeaderboardScreen;
  claim?: ClaimScreen;

  enterTitle(): void {
    this.hud.root.hidden = true;
    this.minimap.hide();
    this.hud.setPaused(false);
    this.pause.hide();
    this.cameraToggle.hide();
    this.title.show();
  }

  enterRace(currentCameraMode: 'chase' | 'topDown'): void {
    this.title.hide();
    this.pause.hide();
    this.hud.show();
    this.minimap.show();
    this.hud.setPaused(false);
    this.cameraToggle.setMode(currentCameraMode);
    this.cameraToggle.show();
  }

  enterPause(): void {
    this.hud.setPaused(true);
    this.hud.setWrongWay(false);
    this.pause.show();
  }

  exitPause(): void {
    this.hud.setPaused(false);
    this.pause.hide();
  }

  /** Fields this system contributes to the window.__twin debug surface. */
  debugEntries(): Record<string, unknown> {
    return {
      hud: this.hud,
      minimap: this.minimap,
    };
  }
}
